import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ColumnDef } from "@tanstack/react-table";

import { DialogoAlerta } from "@/components/alert-dialog";
import { DataTable } from "@/components/data-table";
import { useGlobalLoader } from "@/components/GlobalLoader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { AdminContext } from "@/contexts/AdminContext";
import { useSeriais } from "@/contexts/SeriaisContext";
import { alert } from "@/contexts/AlertModalContext";
import type { SerialResponseItem } from "@/types/serial-response-item";
import {
  Hash,
  Inbox,
  Pencil,
  RefreshCw,
  ShieldAlert,
  Trash2,
} from "lucide-react";

// Célula de edição extraída (evita mutação do objeto e usa controlled state)
function EditSerialCell({
  item,
  isLoading,
  editarSeriais,
}: {
  item: SerialResponseItem;
  isLoading: boolean;
  editarSeriais: (seriais: SerialResponseItem[]) => Promise<unknown>;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [gtin, setGtin] = useState(item.gtin);
  const [serial, setSerial] = useState(item.serial);

  useEffect(() => {
    if (!open) {
      setGtin(item.gtin);
      setSerial(item.serial);
    }
  }, [open, item.gtin, item.serial]);

  const handleSalvar = async () => {
    if (!gtin.trim()) {
      alert.error(t("serializacao.codeEmpty"));
      return;
    }
    if (!Number.isFinite(serial) || serial < 0) {
      alert.error(t("serializacao.serialInvalid"));
      return;
    }
    await editarSeriais([{ ...item, gtin: gtin.trim(), serial }]);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm">
          <Pencil className="h-3.5 w-3.5" />
          {t("serializacao.edit")}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80">
        <div className="grid gap-3">
          <div className="grid gap-1.5">
            <Label htmlFor={`serial-codigo-${item._id}`} className="text-xs">
              {t("serializacao.codeGtin")}
            </Label>
            <Input
              id={`serial-codigo-${item._id}`}
              disabled={isLoading}
              type="text"
              value={gtin}
              onChange={(e) => setGtin(e.target.value)}
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor={`serial-valor-${item._id}`} className="text-xs">
              {t("serializacao.serial")}
            </Label>
            <Input
              id={`serial-valor-${item._id}`}
              disabled={isLoading}
              type="number"
              min={0}
              value={serial}
              onChange={(e) => {
                const v = e.target.value;
                setSerial(v === "" ? 0 : parseInt(v, 10));
              }}
            />
          </div>
          <Button size="sm" onClick={handleSalvar} disabled={isLoading}>
            {t("serializacao.saveChanges")}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function Serializacao() {
  const { t } = useTranslation();
  const { isAdmin, requireAdmin } = useContext(AdminContext)!;
  const { seriais, coletaSeriais, editarSeriais, apagarSerial } = useSeriais();
  const { isLoading } = useGlobalLoader();
  const [isOpenAlertDialog, setIsOpenAlertDialog] = useState(false);
  const [isOpenDialogApagarTodos, setIsOpenDialogApagarTodos] = useState(false);
  const [serialSelecionado, setSerialSelecionado] =
    useState<SerialResponseItem | null>(null);

  useEffect(() => {
    requireAdmin(() => {
      // Ação a ser executada se o usuário for admin
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sincronizar = async () => {
    await coletaSeriais();
    alert.success(t("serializacao.syncSuccess"));
  };

  const handleApagarTodos = async () => {
    setIsOpenDialogApagarTodos(false);
    if (isLoading) return;
    if (seriais.length === 0) {
      alert.error(t("serializacao.noSerialsToDelete"));
      return;
    }
    try {
      await Promise.all(seriais.map((serial) => apagarSerial(serial)));
      alert.success(t("serializacao.allDeleted"));
    } catch {
      alert.error(t("serializacao.deleteError"));
    }
  };

  const columns: ColumnDef<SerialResponseItem>[] = [
    {
      id: "index",
      header: () => <div className="w-10">#</div>,
      cell: ({ row }) => (
        <span className="text-xs text-muted-foreground">{row.index + 1}</span>
      ),
    },
    {
      accessorKey: "gtin",
      header: t("serializacao.codeGtin"),
      cell: ({ row }) => (
        <span className="text-sm font-medium">{row.original.gtin}</span>
      ),
    },
    {
      accessorKey: "serial",
      header: t("serializacao.currentSerial"),
      cell: ({ row }) => (
        <Badge variant="secondary" className="font-medium">
          {row.original.serial}
        </Badge>
      ),
    },
    {
      id: "actions",
      header: () => (
        <div className="text-right">{t("serializacao.actions")}</div>
      ),
      cell: ({ row }) => {
        const item = row.original;
        return (
          <div className="flex items-center gap-2 justify-end">
            <EditSerialCell
              item={item}
              isLoading={isLoading}
              editarSeriais={editarSeriais}
            />
            <Button
              disabled={isLoading}
              variant="outline"
              size="sm"
              className="text-red-600 hover:text-red-700 hover:bg-red-500/10"
              onClick={() => {
                setSerialSelecionado(item);
                setIsOpenAlertDialog(true);
              }}
            >
              <Trash2 className="h-3.5 w-3.5" />
              {t("serializacao.delete")}
            </Button>
          </div>
        );
      },
    },
  ];

  // ---- Acesso restrito ----
  if (!isAdmin) {
    return (
      <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6 px-4 lg:px-6">
        <Card>
          <CardContent className="py-16 flex flex-col items-center justify-center gap-3 text-muted-foreground">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-destructive/10">
              <ShieldAlert className="h-6 w-6 text-destructive" />
            </div>
            <div className="text-center max-w-sm">
              <p className="text-sm font-medium text-foreground">
                {t("serializacao.restrictedTitle")}
              </p>
              <p className="text-xs">{t("serializacao.restrictedDesc")}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6 px-4 lg:px-6">
      <DialogoAlerta
        description={t("serializacao.deleteAllConfirm")}
        title={t("serializacao.deleteAllTitle")}
        isOpen={isOpenDialogApagarTodos}
        onConfirm={handleApagarTodos}
        onOpenChange={() => {
          setIsOpenDialogApagarTodos(false);
          if (serialSelecionado) setSerialSelecionado(null);
        }}
        key="dialogo-alerta-apagar-serial-todos"
      />
      <DialogoAlerta
        description={t("serializacao.deleteOneConfirm")}
        title={t("serializacao.deleteOneTitle")}
        isOpen={isOpenAlertDialog}
        onConfirm={() => {
          if (serialSelecionado) apagarSerial(serialSelecionado);
        }}
        onOpenChange={() => {
          setIsOpenAlertDialog(false);
          if (serialSelecionado) setSerialSelecionado(null);
        }}
        key="dialogo-alerta-apagar-serial"
      />

      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h2 className="text-xl font-semibold tracking-tight">
            {t("serializacao.title")}
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            {t("serializacao.subtitle")}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={sincronizar}
            disabled={isLoading}
          >
            <RefreshCw
              className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`}
            />
            <span className="hidden sm:inline">{t("common2.sync")}</span>
          </Button>

          <Button
            variant="destructive"
            size="sm"
            onClick={() => setIsOpenDialogApagarTodos(true)}
            disabled={isLoading || seriais.length === 0}
            title={
              seriais.length === 0
                ? t("serializacao.noSerialsToDeleteTitle")
                : t("serializacao.deleteAllTitle")
            }
          >
            <Trash2 className="h-4 w-4" />
            <span className="hidden sm:inline">
              {t("serializacao.deleteAll")}
            </span>
          </Button>
        </div>
      </div>

      {/* Card com seriais */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Hash className="h-4 w-4 text-muted-foreground" />
              <CardTitle className="text-sm font-medium">
                {t("serializacao.registeredSerials")}
              </CardTitle>
            </div>
            <Badge variant="secondary" className="font-medium">
              {t("serializacao.codesCount", { count: seriais.length })}
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          {seriais.length === 0 ? (
            <div className="py-16 flex flex-col items-center justify-center gap-3 text-muted-foreground">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-muted">
                <Inbox className="h-6 w-6" />
              </div>
              <div className="text-center max-w-sm">
                <p className="text-sm font-medium text-foreground">
                  {t("serializacao.noSerials")}
                </p>
                <p className="text-xs">{t("serializacao.noSerialsDesc")}</p>
              </div>
            </div>
          ) : (
            <DataTable columns={columns} data={seriais} />
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default Serializacao;
